import { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Drawer, Input, Select } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import type { CreditPayment, CreditSettings } from '@deposits/shared';
import { Money } from '../../components/Money';
import { useCreateCreditDeposit } from '../../hooks/useCredit';
import { useLocationContext } from '../../auth/LocationContext';
import { useProjectContext } from '../../auth/ProjectContext';
import { todayIsrael } from '../../utils/dates';

interface Props {
  open: boolean;
  /** All of one company (the table enforces it). */
  selected: CreditPayment[];
  settings: CreditSettings | null;
  onClose: () => void;
  onCreated: (depositId: number, depositNumber: number) => void;
}

/** The company's ledger account + default bank from the settings. */
function companyConfig(settings: CreditSettings | null, key: string | null) {
  if (!settings || !key) return null;
  if (settings.grouping === 'acquirer') {
    const a = settings.acquirers.find((x) => `a${x.id}` === key);
    return a ? { ledger: a.ledger_account, bankId: a.default_bank_id } : null;
  }
  const i = settings.issuers.find((x) => x.name === key);
  return { ledger: i?.ledger_account ?? null, bankId: i?.default_bank_id ?? null };
}

export function NewCreditDepositDrawer({ open, selected, settings, onClose, onCreated }: Props) {
  const { selectedLocationId, selectedLocation } = useLocationContext();
  const { selectedProjectId, selectedProject } = useProjectContext();
  const company = selected[0] ?? null;
  const config = companyConfig(settings, company?.company_key ?? null);
  const banks = settings?.banks ?? [];
  const total = useMemo(() => selected.reduce((s, p) => s + p.amount, 0), [selected]);

  const [depositDate, setDepositDate] = useState<Dayjs>(() => dayjs(todayIsrael()));
  const [bankId, setBankId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  // Reset on open; the bank starts at the company's default (can be changed).
  useEffect(() => {
    if (!open) return;
    setDepositDate(dayjs(todayIsrael()));
    setBankId(config?.bankId ?? null);
    setNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = useCreateCreditDeposit();
  const bank = banks.find((b) => b.id === bankId) ?? null;
  const problem = !config?.ledger
    ? `לא הוגדר מספר כרטסת עבור ${company?.company_name ?? 'החברה'}. הגדר אותו בהגדרות.`
    : banks.length === 0
      ? 'לא הוגדרו בנקים. הוסף בנק בהגדרות.'
      : total <= 0
        ? 'סכום ההפקדה חייב להיות גדול מאפס.'
        : null;

  const submit = () => {
    if (selectedLocationId == null || bankId == null || problem) return;
    create.mutate(
      {
        location_id: selectedLocationId,
        project_id: selectedProjectId ?? undefined,
        deposit_date: depositDate.format('YYYY-MM-DD'),
        bank_id: bankId,
        notes: notes.trim() || null,
        external_receipt_ids: selected.map((p) => p.external_receipt_id),
      },
      {
        onSuccess: (created) => {
          toast.success(`הפקדה מספר ${created.deposit_number} נוצרה`);
          if (created.pdf_status === 'failed') toast.error('ההפקדה נוצרה אך הפקת ה-PDF נכשלה — נסה שוב מרשימת ההפקדות');
          onCreated(created.id, created.deposit_number);
        },
        onError: (err) => {
          toast.error(err.message || 'שגיאה ביצירת ההפקדה');
          if (err.message?.includes('כבר הופקדו')) onClose();
        },
      },
    );
  };

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">הפקדת אשראי חדשה</span>}
      placement="left"
      open={open}
      onClose={onClose}
      width={520}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        <div className="bg-blue-bg border border-border rounded p-4 mb-6">
          <div className="font-semibold mb-3">סיכום</div>
          <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-base">
            <Row label="סניף">{selectedLocation?.name ?? '—'}</Row>
            {selectedProject && <Row label="פרויקט">{selectedProject.name}</Row>}
            <Row label={settings?.grouping === 'acquirer' ? 'חברה סולקת' : 'חברה מנפיקה'}>
              {company?.company_name ?? '—'}
            </Row>
            <Row label="כרטסת חברה">
              <span dir="ltr">{config?.ledger ?? '—'}</span>
            </Row>
            <Row label="כמות תשלומים">
              <span dir="ltr">{selected.length}</span>
            </Row>
            <Row label='סה"כ להפקדה' bold>
              <Money amount={total} />
            </Row>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="תאריך הפקדה">
            <DatePicker
              value={depositDate}
              onChange={(d) => d && setDepositDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Field>
          <Field label="בנק">
            <Select<number>
              value={bankId ?? undefined}
              onChange={setBankId}
              placeholder="בחר בנק"
              options={banks.map((b) => ({ value: b.id, label: `${b.name} (${b.ledger_account})` }))}
              style={{ width: '100%' }}
            />
            {bank && config?.bankId != null && bank.id !== config.bankId && (
              <div className="text-small text-secondary-text mt-1">שונה מבנק ברירת המחדל של החברה</div>
            )}
          </Field>
          <Field label="הערות">
            <Input.TextArea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1000} />
          </Field>

          {problem && <div className="text-small text-error-red">{problem}</div>}

          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={onClose}>ביטול</Button>
            <Button
              type="primary"
              onClick={submit}
              loading={create.isPending}
              disabled={!!problem || bankId == null}
            >
              צור הפקדה
            </Button>
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function Row({ label, children, bold }: { label: string; children: React.ReactNode; bold?: boolean }) {
  return (
    <>
      <div className="text-secondary-text">{label}</div>
      <div className={`text-end ${bold ? 'font-semibold' : ''}`}>{children}</div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-small text-main-text mb-1">{label}</div>
      {children}
    </div>
  );
}
